import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Button,
    Avatar,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    ArrowForward as ArrowIcon,
    Login as LoginIcon,
    Public as PublicIcon,
    Lock as GroupIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Close as ClearIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import {
    useGetProfileQuery,
    useGetAppCardsQuery,
    useGetSettingsQuery,
    useRefreshLoginMutation,
} from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { styleHelpers, cardFallbackGradient } from '@/theme';
import type { AppCard } from '@/types/appCard';
import Header from '@/components/Header';

const FAVORITES_KEY = 'favoriteApps';

const readFavorites = (): string[] => {
    try {
        const raw = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
        return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : [];
    } catch {
        // Corrupt or unavailable storage must never keep the page from rendering.
        return [];
    }
};

/** Small uppercase label over a value; used for the profile meta strip. */
const Meta: FC<{ label: string; value?: ReactNode }> = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
            }}
        >
            {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {value || '—'}
        </Typography>
    </Box>
);

type AppTileProps = {
    app: AppCard;
    href: string;
    isAuthenticated: boolean;
    isFavorite: boolean;
    onToggleFavorite: (id: string) => void;
};

const AppTile: FC<AppTileProps> = ({ app, href, isAuthenticated, isFavorite, onToggleFavorite }) => {
    const { LL } = useI18nContext();
    const favoriteLabel = isFavorite ? LL.removeFavorite() : LL.addFavorite();
    const openLabel = LL.openApp({ application: app.title });

    return (
        <Card
            sx={(theme) => ({
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                '&:hover': {
                    transform: theme.brand.effects.hoverTransform,
                    borderColor: theme.palette.primary.main,
                    boxShadow: theme.brand.effects.shadowGlow,
                },
                '&:hover .tile-media': { transform: theme.brand.effects.mediaZoom },
                '&:hover .tile-arrow': { transform: 'translateX(4px)' },
                '&:focus-within': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: theme.brand.effects.shadowGlow,
                },
            })}
        >
            <Box sx={{ position: 'relative', height: 168, overflow: 'hidden' }}>
                <Box
                    className="tile-media"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        // Without an image every tile would look identical, so fall
                        // back to a gradient derived from the title.
                        // Quote the URL: an unquoted url() breaks on any image
                        // address containing parentheses, spaces or quotes.
                        backgroundImage: app.image
                            ? `url("${app.image.replace(/"/g, '%22')}")`
                            : cardFallbackGradient(app.title || app.id),
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        transition: 'transform 0.5s cubic-bezier(.4,0,.2,1)',
                        willChange: 'transform',
                    }}
                />
                {/* Scrim keeps the title readable over any artwork. */}
                <Box
                    sx={(theme) => ({
                        position: 'absolute',
                        inset: 0,
                        background: theme.brand.gradients.scrim,
                    })}
                />
                <Typography
                    variant="h6"
                    sx={{
                        position: 'absolute',
                        left: 20,
                        right: 20,
                        bottom: 14,
                        color: 'common.white',
                        fontWeight: 800,
                        letterSpacing: '-0.015em',
                        textShadow: '0 2px 12px rgba(0,0,0,0.65)',
                    }}
                >
                    {app.title}
                </Typography>
            </Box>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {app.description && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {app.description}
                    </Typography>
                )}

                {isAuthenticated && (app.visibility === 'public' || !!app.groups?.length) && (
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }} useFlexGap>
                        {app.visibility === 'public' && (
                            <Chip
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<PublicIcon />}
                                label={LL.public()}
                            />
                        )}
                        {app.groups?.map((group) => (
                            <Chip
                                key={group}
                                size="small"
                                variant="outlined"
                                icon={<GroupIcon />}
                                label={group}
                            />
                        ))}
                    </Stack>
                )}

                <Stack
                    direction="row"
                    sx={{
                        mt: 'auto',
                        pt: 0.5,
                        alignItems: 'center',
                        gap: 0.75,
                        color: 'primary.light',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                    }}
                >
                    {openLabel}
                    <ArrowIcon
                        className="tile-arrow"
                        fontSize="small"
                        sx={(theme) => ({ transition: theme.brand.transitions.standard })}
                    />
                </Stack>
            </CardContent>

            {/*
              Stretched link: a single anchor covering the card. This keeps one
              entry in the accessibility tree and makes the whole tile clickable,
              instead of nesting a button inside a link.
            */}
            <Box
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={openLabel}
                sx={{ position: 'absolute', inset: 0, zIndex: 1, borderRadius: 'inherit' }}
            />

            {/* Sits above the stretched link so favouriting never follows it. */}
            {isAuthenticated && (
                <Tooltip title={favoriteLabel}>
                    <IconButton
                        aria-label={favoriteLabel}
                        aria-pressed={isFavorite}
                        onClick={() => onToggleFavorite(app.id)}
                        size="small"
                        sx={(theme) => ({
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            // Sits on card artwork, so it stays light in both modes.
                            color: isFavorite ? theme.palette.warning.light : '#fff',
                            bgcolor: theme.brand.backgrounds.glassStrong,
                            border: `1px solid ${theme.brand.backgrounds.onBrandBorderStrong}`,
                            backdropFilter: theme.brand.effects.backdropBlur,
                            '&:hover': { bgcolor: theme.brand.backgrounds.glassHover },
                        })}
                    >
                        {isFavorite ? (
                            <StarIcon fontSize="small" />
                        ) : (
                            <StarBorderIcon fontSize="small" />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </Card>
    );
};

const HomePage: FC = () => {
    const { LL } = useI18nContext();
    const [relogging, setRelogging] = useState(false);
    const [query, setQuery] = useState('');
    const [favorites, setFavorites] = useState<string[]>(readFavorites);

    const { data: settings } = useGetSettingsQuery();
    const { data: user, isLoading: userLoading, error: userError } = useGetProfileQuery();
    const { data: appsData, isLoading: appsLoading, error: appsError } = useGetAppCardsQuery();
    const [refreshLogin] = useRefreshLoginMutation();

    const apps = appsData?.apps || [];
    const isAuthenticated = !!user && !userError;

    // Every hook must run before the loading early-return below, or React sees a
    // different hook count between renders.
    useEffect(() => {
        try {
            localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
        } catch {
            // Storage can be unavailable (private mode); favourites stay in memory.
        }
    }, [favorites]);

    const toggleFavorite = useCallback((id: string) => {
        setFavorites((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
    }, []);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return apps;
        return apps.filter((app) =>
            [app.title, app.description, ...(app.groups ?? [])]
                .filter(Boolean)
                .some((field) => String(field).toLowerCase().includes(needle))
        );
    }, [apps, query]);

    if (userLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const resolveUrlVars = (url: string) =>
        (url ?? '')
            .replace('{username}', encodeURIComponent(user?.displayName ?? ''))
            .replace('{email}', encodeURIComponent(user?.email ?? ''));

    const gridSx = {
        display: 'grid',
        gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
        },
        gap: { xs: 2.5, md: 3 },
    };

    const favoriteApps = matches.filter((app) => favorites.includes(app.id));
    const otherApps = matches.filter((app) => !favorites.includes(app.id));
    const showFavorites = isAuthenticated && favoriteApps.length > 0;

    const handleReloginToRefresh = async () => {
        if (!isAuthenticated || relogging) return;
        setRelogging(true);
        try {
            const result = await refreshLogin().unwrap();
            if (result?.loginUrl) {
                window.location.href = result.loginUrl;
            }
        } catch {
            // Ignore: user can still use normal login/logout flows
        } finally {
            setRelogging(false);
        }
    };

    const title = settings?.title ?? LL.appTitle();

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Header title={title} />

            <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
                {/* Hero */}
                <Box sx={{ mb: { xs: 5, md: 7 } }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={{ alignItems: { sm: 'center' }, gap: { xs: 2.5, sm: 3 } }}
                    >
                        {isAuthenticated && (
                            <Avatar sx={styleHelpers.roleAvatar(user?.isAdmin ?? false, 72)}>
                                {user?.displayName
                                    ?.split(' ')
                                    .map((e: string) => e.charAt(0).toUpperCase())
                                    .slice(0, 2)
                                    .join('')}
                            </Avatar>
                        )}

                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontSize: { xs: '1.9rem', sm: '2.4rem', md: '2.9rem' },
                                    lineHeight: 1.1,
                                }}
                            >
                                {LL.welcome({ title })}
                            </Typography>
                            {/* Brand accent rule under the headline. */}
                            <Box
                                sx={(theme) => ({
                                    mt: 2,
                                    width: 72,
                                    height: 4,
                                    borderRadius: 999,
                                    background: theme.brand.gradients.accent,
                                })}
                            />
                        </Box>
                    </Stack>

                    {isAuthenticated && (
                        <Card sx={{ mt: 4 }}>
                            <CardContent>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    sx={{
                                        gap: { xs: 2.5, md: 4 },
                                        alignItems: { md: 'center' },
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        sx={{ flexWrap: 'wrap', gap: { xs: 2.5, sm: 4 } }}
                                        useFlexGap
                                    >
                                        <Meta
                                            label={LL.displayName()}
                                            value={user?.displayName}
                                        />
                                        <Meta label={LL.firstName()} value={user?.firstName} />
                                        <Meta label={LL.lastName()} value={user?.lastName} />
                                        <Meta label={LL.email()} value={user?.email} />
                                    </Stack>

                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={handleReloginToRefresh}
                                        disabled={relogging}
                                        sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', md: 'center' } }}
                                    >
                                        {LL.refreshGroupAssignments?.() ?? LL.refreshSession()}
                                    </Button>
                                </Stack>

                                {!!user?.groups?.length && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="overline" sx={styleHelpers.sectionLabel}>
                                            {LL.groups()}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            sx={{ flexWrap: 'wrap', gap: 0.75 }}
                                            useFlexGap
                                        >
                                            {user.groups.map((group: string) => (
                                                <Chip
                                                    key={group}
                                                    label={group}
                                                    size="small"
                                                    color="primary"
                                                    sx={{ cursor: 'default' }}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </Box>

                {/* Applications Section */}
                <Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        sx={{
                            mb: 3,
                            alignItems: { sm: 'center' },
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontSize: { xs: '1.35rem', md: '1.6rem' } }}>
                            {LL.applications()}
                        </Typography>

                        {apps.length > 0 && (
                            <TextField
                                size="small"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={LL.searchApps()}
                                aria-label={LL.searchApps()}
                                sx={{ minWidth: { sm: 300 } }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: query ? (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    aria-label={LL.clearSearch()}
                                                    onClick={() => setQuery('')}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ) : null,
                                    },
                                }}
                            />
                        )}
                    </Stack>

                    {appsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : appsError ? (
                        <Alert severity="error">{LL.error()}</Alert>
                    ) : (
                        <>
                            {showFavorites && (
                                <>
                                    <Typography variant="overline" sx={styleHelpers.sectionLabel}>
                                        {LL.favorites()}
                                    </Typography>
                                    <Box sx={gridSx}>
                                        {favoriteApps.map((app: AppCard) => (
                                            <AppTile
                                                key={app.id}
                                                app={app}
                                                href={resolveUrlVars(app.link)}
                                                isAuthenticated={isAuthenticated}
                                                isFavorite
                                                onToggleFavorite={toggleFavorite}
                                            />
                                        ))}
                                    </Box>
                                    <Typography
                                        variant="overline"
                                        sx={{ ...styleHelpers.sectionLabel, mt: 5 }}
                                    >
                                        {LL.otherApplications()}
                                    </Typography>
                                </>
                            )}

                            <Box sx={gridSx}>
                                {/* Login Card */}
                                {!isAuthenticated && (
                                    <Card
                                        sx={(theme) => ({
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                            transition: theme.brand.transitions.smooth,
                                            '&:hover': {
                                                transform: theme.brand.effects.hoverTransform,
                                                borderColor: theme.palette.primary.main,
                                                boxShadow: theme.brand.effects.shadowGlow,
                                            },
                                        })}
                                    >
                                        <Stack
                                            direction="row"
                                            sx={(theme) => ({
                                                height: 168,
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: theme.brand.gradients.primary,
                                                color: theme.brand.onBrand,
                                            })}
                                        >
                                            <Box sx={styleHelpers.iconBox(72, 'onBrand')}>
                                                <LoginIcon sx={{ fontSize: 34 }} />
                                            </Box>
                                        </Stack>
                                        <CardContent
                                            sx={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1.5,
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                {LL.loginWithOIDC()}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {LL.loginRequired()}
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                startIcon={<LoginIcon />}
                                                sx={{ mt: 'auto' }}
                                                onClick={() => {
                                                    const redirectPath =
                                                        window.location.pathname +
                                                        window.location.search;
                                                    window.location.href =
                                                        '/oidc?redirect=' +
                                                        encodeURIComponent(redirectPath);
                                                }}
                                            >
                                                {LL.loginWithOIDC()}
                                            </Button>
                                        </CardContent>
                                    </Card>
                                )}

                                {otherApps.map((app: AppCard) => (
                                    <AppTile
                                        key={app.id}
                                        app={app}
                                        href={resolveUrlVars(app.link)}
                                        isAuthenticated={isAuthenticated}
                                        isFavorite={false}
                                        onToggleFavorite={toggleFavorite}
                                    />
                                ))}
                            </Box>

                            {matches.length === 0 && query.trim() !== '' && (
                                <Alert severity="info" sx={{ mt: 3 }}>
                                    {LL.noSearchResults({ query: query.trim() })}
                                </Alert>
                            )}
                            {apps.length === 0 && query.trim() === '' && isAuthenticated && (
                                <Alert severity="info" sx={{ mt: 3 }}>
                                    {LL.noAppsAvailable()}
                                </Alert>
                            )}
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default HomePage;
